import { CommonModule } from "@angular/common";
import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  signal,
  untracked,
  viewChild,
  WritableSignal,
} from "@angular/core";
import { ViewerSettingsService } from "../viewer-settings.store";
import { NzCardComponent } from "ng-zorro-antd/card";
import { isMarkerVisible, JobStateService } from "../job-state.store";
import * as d3 from "d3";
import { stripDash } from "../model-new.component";
import { toPng } from "html-to-image";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzDropDownModule } from "ng-zorro-antd/dropdown";
import { NzFormModule } from "ng-zorro-antd/form";
import { NzInputNumberComponent } from "ng-zorro-antd/input-number";
import { VerticalProfileService } from "./vertical-profile.service";
import { FormsModule } from "@angular/forms";
import { rxResource } from "@angular/core/rxjs-interop";
import { CachedProjectService } from "../../../../shared/services/cached-project.service";
import { map, of } from "rxjs";
import { LogMarkers } from "../../../../models/coordinate";
import { NzCheckboxModule } from "ng-zorro-antd/checkbox";
import { NzButtonComponent } from "ng-zorro-antd/button";
import { ScreenshotFrameService } from "../../../../shared/services/screenshot-frame.service";

interface WellMarker {
  m: string; // label of marker
  z: number; // depth of marker
  visibility: boolean; // visibility of marker
}

@Component({
  selector: "app-vertical-profile",
  imports: [
    CommonModule,
    FormsModule,
    NzCardComponent,
    NzIconModule,
    NzDropDownModule,
    NzFormModule,
    NzCheckboxModule,
    NzButtonComponent,
    NzInputNumberComponent,
  ],
  templateUrl: "./vertical-profile.component.html",
  styleUrl: "./vertical-profile.component.less",
})
export class VerticalProfileComponent {
  // ─────────────────────────────────────────────────────────────
  // SERVICES
  // ─────────────────────────────────────────────────────────────

  protected viewerSettingsService = inject(ViewerSettingsService);
  protected jobStateService = inject(JobStateService);
  protected verticalProfileService = inject(VerticalProfileService);
  private cachedProjectService = inject(CachedProjectService);
  private destroyRef = inject(DestroyRef);
  private screenshotFrame = inject(ScreenshotFrameService);

  /** Reference to the host div that D3 renders into. */
  private chartContainer =
    viewChild.required<ElementRef<HTMLDivElement>>("chartContainer");

  // ─────────────────────────────────────────────────────────────
  // ANGULAR STATE
  // ─────────────────────────────────────────────────────────────

  /** Resolved chart height based on the current vertical-profile display mode. */
  private height = computed(() => {
    if (this.viewerSettingsService.verticalProfileMode() === "besideInline")
      return this.viewerSettingsService.viewerHeight();
    else if (this.viewerSettingsService.verticalProfileMode() === "show")
      return this.viewerSettingsService.depthViewerHeight();
    else return 0;
  });

  /** Cell size in metres from the model grid. Used to convert depth indices → real depth. */
  private dx = computed(() => this.jobStateService.jobData().modelGrid.dx);

  /** Currently selected crossline (1-based); drives which column is extracted as the velocity profile. */
  private crossline = computed(() => this.jobStateService.crossline());

  /** Maximum depth value in real-world units — exposed to the template for the depth-range input max. */
  protected depthMax = computed(
    () =>
      this.jobStateService.jobData().modelGrid.dx *
      this.jobStateService.jobData().modelGrid.nx3,
  );

  /** The currently selected well log, forwarded to the template for the header display. */
  protected selectedLog = computed(() => this.jobStateService.selectedLog());

  // --- UI toggles (bound to checkboxes in the settings dropdown) ---

  /** Controls visibility of the red velocity-profile line. */
  protected showVelocityProfile = signal<boolean>(true);

  /** Controls visibility of the blue sonic-log line. */
  protected showSonicLog = signal<boolean>(true);

  // --- Hover readout (populated by the mousemove handler) ---

  /** Current depth under the cursor in real-world units, displayed in the metrics table. */
  protected depth: number;

  /** Velocity value at the current cursor depth, displayed in the metrics table. */
  protected profileValue: any;

  // ─────────────────────────────────────────────────────────────
  // DATA LAYER
  // ─────────────────────────────────────────────────────────────

  /**
   * Lazily fetches and caches the sonic log for the selected well.
   * Re-fetches automatically when the selected log id or frequency changes.
   * Maps raw API DTO coordinates to `{ depth, velocity }` objects.
   */
  private wellLogResource = rxResource({
    params: () => ({
      logId: this.jobStateService.selectedLog()?.id,
      logFrequency: this.jobStateService.logFrequency(),
    }),
    stream: ({ params }) => {
      const { logId, logFrequency } = params;
      if (!logId) return of(null);
      return this.cachedProjectService.getSonicLog(logId, logFrequency).pipe(
        map((res: any) => {
          return {
            log: res.log.map((dp) => ({ depth: dp.x, velocity: dp.y })),
            markers: res.markers,
          };
        }),
      );
    },
  });

  /** Raw 2-D slice from the velocity model at the current inline. */
  private sliceData = computed(() =>
    this.verticalProfileService.inlineSliceData(),
  );

  /**
   * Extracts a 1-D velocity column from the current inline slice at the active crossline.
   * Also pushes the min/max velocity into the service so x-axis scales stay current.
   * `updateVelocityMinMax` is called with `untracked` to avoid a circular dependency.
   */
  private profileData = computed(() => {
    let sliceData = this.sliceData();
    let cx = this.crossline() - 1;
    if (sliceData.length > 0 && cx >= 0) {
      const velocities = new Array(sliceData.length);
      let min = Infinity;
      let max = -Infinity;

      for (let i = 0; i < sliceData.length; i++) {
        const val = sliceData[i][cx];
        velocities[i] = val;
        if (val > max) max = val;
        if (val < min) min = val;
      }
      // untracked to prevent circular dependency. and only update min/max IF sync is off. causer otherwise we wanna preserve
      untracked(() => {
        if (!this.jobStateService.synchroniseSettings()) this.updateVelocityMinMax(min, max)
      });
      return velocities;
    }
    return [];
  });

  /** Stored well markers from the last successful log fetch; used by `renderWellMarkers`. */
  private wellMarkers = signal<WellMarker[]>([]);

  // ─────────────────────────────────────────────────────────────
  // D3 / CHART INFRASTRUCTURE
  // ─────────────────────────────────────────────────────────────

  // --- Static config ---

  /** Inner chart margins; determines the usable drawing area inside the SVG. */
  private margin = { top: 5, right: 10, bottom: 33, left: 50 };

  // --- Lifecycle flag ---

  /** Set to true after `initializeGraph()` completes; guards all effects from running before the SVG exists. */
  private isInitialized = signal<boolean>(false);

  // --- D3 selections (all created in initializeGraph) ---

  private svgSelection!: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  private chartGroup!: d3.Selection<SVGGElement, unknown, null, undefined>;
  /** Clipped group; direct parent of the data lines so they honour the chart boundary. */
  private zoomableContentGroup: d3.Selection<
    SVGGElement,
    unknown,
    null,
    undefined
  >;
  private xAxisGroup!: d3.Selection<SVGGElement, unknown, null, undefined>;
  private yAxisGroup!: d3.Selection<SVGGElement, unknown, null, undefined>;
  private xGridGroup!: d3.Selection<SVGGElement, unknown, null, undefined>;
  private yGridGroup!: d3.Selection<SVGGElement, unknown, null, undefined>;
  /** Red velocity-profile path drawn from the model data. */
  private profileLine: d3.Selection<SVGPathElement, any, null, undefined>;
  /** Sample-point dots overlaid on the sonic-log line when showSonicSamples is on. */
  private sonicDotsGroup: d3.Selection<SVGGElement, unknown, null, undefined>;
  /** Blue sonic-log path drawn from the well log data. */
  private sonicLine: d3.Selection<SVGPathElement, any, null, undefined>;
  /** Group containing formation marker lines and labels. */
  private sonicMarkersG: d3.Selection<SVGGElement, unknown, null, undefined>;
  /** Horizontal dashed line that tracks the active depth indicator. */
  private hLine: d3.Selection<SVGLineElement, unknown, null, undefined>;
  /** Crosshair (two lines + dot) shown on hover; toggled by mouseover/out. */
  private hoverCrosshairGroup!: d3.Selection<
    SVGGElement,
    unknown,
    null,
    undefined
  >;

  private eventsOverlay: d3.Selection<SVGRectElement, unknown, null, undefined>;

  // --- Zoom ---

  private zoomBehavior = d3.zoom<SVGSVGElement, unknown>();

  // --- Scales (reactive; recalculate when domain or range inputs change) ---

  /** Pixel width of the inner chart area (kept in sync with the container width). */
  private width = signal<number>(null);

  /**
   * D3 linear scale mapping velocity values → pixel x-coordinates.
   * Domain comes from the shared `VerticalProfileService` so all views stay in sync.
   */
  private xScale = computed(() => {
    if (this.width()) {
      return d3
        .scaleLinear()
        .domain([
          this.verticalProfileService.xMin(),
          this.verticalProfileService.xMax(),
        ])
        .range([0, this.width()]);
    }
  });

  /**
   * D3 linear scale mapping depth index → pixel y-coordinates.
   * Domain is the user-controlled depth range (in grid-index units).
   */
  private yScale = computed(() => {
    if (this.height()) {
      return d3
        .scaleLinear()
        .domain([
          this.jobStateService.rangesStart.depth() / this.dx(),
          this.jobStateService.rangesEnd.depth() / this.dx(),
        ])
        .range([0, this.height()]);
    }
  });

  /**
   * Reusable D3 line generator for the sonic log.
   * Kept as a field so `scaleChangeLineUpdates` can re-apply new scales
   * without refetching or rebinding data.
   */
  private sonicLineGenerator: d3.Line<{ depth: number; velocity: number }>;

  // ─────────────────────────────────────────────────────────────
  // CONSTRUCTOR & EFFECTS
  // ─────────────────────────────────────────────────────────────

  constructor() {
    // Initialise the SVG after the first render so the container has real dimensions.
    afterNextRender(() => {
      this.initializeGraph();
      this.isInitialized.set(true);
      this.observeContainerWidth();
    });

    // Re-plot the velocity profile whenever the underlying data changes.
    effect(() => {
      const data = this.profileData();
      if (this.isInitialized() && data.length > 0) {
        untracked(() => {
          this.plotVerticalProfile(data);
        });
      }
    });

    // Re-draw axes and re-scale lines whenever xScale, yScale, or axisUnits change.
    // Line updates are untracked so a data change alone doesn't also trigger this branch.
    effect(() => {
      if (this.isInitialized()) {
        const xScale = this.xScale();
        const yScale = this.yScale();
        this.renderAxes(xScale, yScale, this.viewerSettingsService.axisUnits());

        // update the profile + sonic line. This happens mainly when the x or y scale change
        // which change when the limits change for x / y
        untracked(() => this.scaleChangeLineUpdates(xScale, yScale));
      }
    });

    // Keep the y-axis label in sync with the selected unit (km / kft).
    effect(() => {
      if (this.isInitialized()) {
        this.yAxisGroup
          .select("text.y-label")
          .text(`Depth (${this.viewerSettingsService.axisUnits()})`);
      }
    });

    // Plot the well log once it loads (or clears if the resource emits null).
    effect(() => {
      if (this.isInitialized()) {
        // null is a valid value here
        if (
          !this.wellLogResource.error() &&
          this.wellLogResource.value() !== undefined
        ) {
          this.plotWellLog(this.wellLogResource.value());
        }
      }
    });

    // Toggle profile-line visibility via a CSS display change — no redraw needed.
    effect(() => {
      if (this.isInitialized()) {
        this.profileLine?.style(
          "display",
          this.showVelocityProfile() ? null : "none",
        );
      }
    });

    // Toggle sonic-log visibility via a CSS display change — no redraw needed.
    effect(() => {
      if (this.isInitialized()) {
        this.sonicLine?.style("display", this.showSonicLog() ? null : "none");
      }
    });

    // Re-render the sonic path and dots when the sample-points toggle changes.
    effect(() => {
      if (this.isInitialized()) {
        const showSonicSamples = this.viewerSettingsService.showSonicSamples();

        // Sonic line: toggle gap-breaking
        if (this.sonicLineGenerator) {
          const GAP_THRESHOLD = 100;
          if (!showSonicSamples) {
            this.sonicLineGenerator.defined((d: any, i, arr) => {
              if (i === 0) return true;
              const prev: any = arr[i - 1];
              return d.depth - prev.depth < GAP_THRESHOLD;
            });
          } else {
            this.sonicLineGenerator.defined(() => true);
          }
          this.sonicLine.attr("d", this.sonicLineGenerator);
        }

        // Sonic dots: render or clear
        const log = untracked(() => this.wellLogResource.value()?.log);
        const [xScale, yScale] = untracked(() => [this.xScale(), this.yScale()]);
        if (showSonicSamples && log?.length && xScale && yScale) {
          this.renderSonicDots(log, xScale, yScale);
        } else {
          this.sonicDotsGroup?.selectAll("circle").remove();
        }
      }
    });

    // Reposition the horizontal depth-indicator line when the active depth or y-scale changes.
    effect(() => {
      if (this.isInitialized()) {
        const yScale = this.yScale();
        this.makeIndicatorLines(yScale);
      }
    });

    effect(() => {
      if (this.isInitialized()) {
        const markersVisibility = this.jobStateService.markersVisibility();
        const showWellMarkers = this.viewerSettingsService.showWellMarkers();
        untracked(() => {
          const markers = this.wellMarkers();
          if (markers.length > 0) {
            this.wellMarkers.set(
              markers.map((m) => ({
                ...m,
                visibility:
                  showWellMarkers && isMarkerVisible(markersVisibility, m.m),
              })),
            );
          }
        });
      }
    });

    effect(() => {
      if (this.isInitialized()) {
        const markers = this.wellMarkers();
        this.renderWellMarkers(markers);
      }
    });

    // Follow the container width (window resizes, layout toggles, and the fixed-size
    // screenshot frames, which temporarily re-lay the viewer grid out at another width)
    effect(() => {
      const w = this.width();
      if (!this.isInitialized() || !w) return;
      untracked(() => {
        const h = this.height();
        this.svgSelection.attr("width", w + this.margin.left + this.margin.right);
        this.svgSelection.select("#vertical-profile-clip rect").attr("width", w);
        this.chartGroup.attr("width", w);
        this.xAxisGroup.select("text.x-label").attr("transform", `translate(${w / 2}, 30)`);
        this.eventsOverlay.attr("width", w);
        this.hoverCrosshairGroup.select("line.x-hover-line").attr("x2", w);
        this.zoomBehavior.translateExtent([[0, 0], [w, h]]).extent([[0, 0], [w, h]]);
        this.makeIndicatorLines(this.yScale());
        this.renderWellMarkers(this.wellMarkers());
        // the xScale effect redraws the axes un-zoomed; re-apply any zoom after it has run
        setTimeout(() => {
          const node = this.eventsOverlay.node();
          const transform = d3.zoomTransform(node);
          if (transform.k !== 1 || transform.x !== 0 || transform.y !== 0) {
            this.eventsOverlay.call(this.zoomBehavior.transform as any, transform);
          }
        });
      });
    });

    effect(() => {
      if (this.isInitialized()) {
        const h = this.height();
        this.svgSelection.attr("height", h + this.margin.top + this.margin.bottom);
        this.svgSelection.select("#vertical-profile-clip rect").attr("height", h);
        this.yAxisGroup
          .select("text.y-label")
          .attr("transform", `translate(-35, ${h / 2}) rotate(-90)`);
        this.eventsOverlay.attr("height", h);
        this.hoverCrosshairGroup.select("line.y-hover-line").attr("y2", h);
      }
    });
  }

  // ─────────────────────────────────────────────────────────────
  // CHART INITIALIZATION
  // ─────────────────────────────────────────────────────────────

  /**
   * Bootstraps the entire SVG structure: clip path, chart groups, data lines,
   * axes, grid, crosshair overlay, and zoom/pan behaviour.
   * Called once via `afterNextRender`.
   */
  private initializeGraph() {
    const container = this.chartContainer()?.nativeElement;
    if (!container) return;

    this.width.set(
      container.clientWidth - (this.margin.left + this.margin.right),
    );

    this.svgSelection = d3
      .select(container)
      .append("svg")
      .attr("width", this.width() + this.margin.left + this.margin.right)
      .attr("height", this.height() + this.margin.top + this.margin.bottom);

    const defs = this.svgSelection.append("defs");
    defs
      .append("clipPath")
      .attr("id", "vertical-profile-clip")
      .append("rect")
      .attr("height", this.height())
      .attr("width", this.width())
      .attr("x", 0)
      .attr("y", 0);

    // chart group will only take the 'available' space that it should have
    this.chartGroup = this.svgSelection
      .append("g")
      .attr("width", this.width())
      .attr("height", this.height())
      .attr("transform", `translate(${this.margin.left},${this.margin.top})`);

    this.zoomableContentGroup = this.chartGroup
      .append("g")
      .attr("clip-path", `url(#vertical-profile-clip)`);

    // the actual profile line, within zoomable to allow zoom capability on the select elements
    this.profileLine = this.zoomableContentGroup
      .append("path")
      .style("vector-effect", "non-scaling-stroke");
    this.sonicDotsGroup = this.zoomableContentGroup
      .append("g")
      .style("vector-effect", "non-scaling-stroke");
    this.sonicLine = this.zoomableContentGroup
      .append("path")
      .style("vector-effect", "non-scaling-stroke");

    this.sonicMarkersG = this.zoomableContentGroup
      .append("g")
      .style("vector-effect", "non-scaling-stroke")
      .raise();

    this.hLine = this.zoomableContentGroup
      .append("g")
      .append("line")
      .attr("class", "h-indicator")
      .attr("stroke", "black")
      .attr("stroke-width", 1)
      .attr("shape-rendering", "crispEdges")
      .style("stroke-dasharray", "5, 3")
      .style("vector-effect", "non-scaling-stroke");

    // make the y and x axes groups and then renderAxes(). that will make the scales and apply
    this.xAxisGroup = this.chartGroup.append("g").attr("class", "x-axis-group");
    this.yAxisGroup = this.chartGroup.append("g").attr("class", "y-axis-group");

    this.yAxisGroup
      .append("text")
      .attr("class", "y-label")
      .attr(
        "transform",
        `
        translate(-35, ${this.height() / 2})
        rotate(-90)
      `,
      )
      .attr("fill", "black")
      .attr("font-size", 12)
      .attr("font-weight", "bold")
      .style("text-anchor", "middle")
      .text(`Depth (${this.viewerSettingsService.axisUnits()})`);
    this.xAxisGroup
      .append("text")
      .attr("class", "x-label")
      .attr("transform", `translate(${this.width() / 2}, 30)`)
      .attr("fill", "black")
      .attr("font-size", 12)
      .attr("font-weight", "bold")
      .style("text-anchor", "middle")
      .text("Velocity (m/s)");

    // Add x grid lines
    this.xGridGroup = this.chartGroup
      .append("g")
      .attr("class", "grid x-grid")
      .attr("transform", `translate(0, ${this.height()})`);
    this.yGridGroup = this.chartGroup.append("g").attr("class", "grid y-grid");

    // the hover functionality for the velocity profile
    this.hoverCrosshairGroup = this.chartGroup
      .append("g")
      .attr("class", "focus")
      .style("display", "none")
      .style("pointer-events", "none");

    // horizontal line (depth)
    this.hoverCrosshairGroup
      .append("line")
      .attr("class", "x-hover-line")
      .attr("stroke", "#2196F3")
      .attr("stroke-width", "1px")
      .attr("stroke-dasharray", "3,3")
      .attr("x1", 0)
      .attr("x2", this.width());
    // verticavl line (velocity)
    this.hoverCrosshairGroup
      .append("line")
      .attr("class", "y-hover-line")
      .attr("stroke", "#2196F3")
      .attr("stroke-width", "1px")
      .attr("stroke-dasharray", "3,3")
      .attr("y1", 0)
      .attr("y2", this.height());
    // The indicator dot
    this.hoverCrosshairGroup
      .append("circle")
      .attr("r", 5)
      .attr("fill", "#ff4d4f") // Standard seismic red
      .attr("stroke", "#fff")
      .attr("stroke-width", "2px");

    // overlay to catch mouse events
    this.eventsOverlay = this.chartGroup
      .append("rect")
      .attr("class", "overlay")
      .attr("width", this.width())
      .attr("height", this.height())
      .attr("fill", "none")
      .attr("pointer-events", "all");

    // Set the zoom and Pan features: how much you can zoom, on which part, and what to do when there is a zoom
    this.zoomBehavior
      .scaleExtent([1, 20]) // Zoom from 1x to 20x
      .filter((event: any) => {
        // Always allow panning (mousedown/mousemove),
        // but only allow wheel zoom if Shift/Ctrl is pressed
        if (event.type === "wheel") {
          return event.shiftKey || event.ctrlKey || event.metaKey;
        }
        return true; // Allow all other mouse interactions
      })
      .on("zoom", (event) => this.handleZoom(event));
    this.zoomBehavior
      .translateExtent([
        [0, 0],
        [this.width(), this.height()],
      ])
      .extent([
        [0, 0],
        [this.width(), this.height()],
      ]);

    this.eventsOverlay.call(this.zoomBehavior as any);
  }

  private observeContainerWidth() {
    const container = this.chartContainer()?.nativeElement;
    if (!container) return;
    let timer: ReturnType<typeof setTimeout>;
    const observer = new ResizeObserver((entries) => {
      const w = Math.round(entries[0].contentRect.width) - (this.margin.left + this.margin.right);
      clearTimeout(timer);
      // same debounce as the slice viewers, so drags don't redraw on every frame
      timer = setTimeout(() => {
        if (w > 0 && w !== this.width()) this.width.set(w);
      }, this.screenshotFrame.capturing ? 0 : 100);
    });
    observer.observe(container);
    this.destroyRef.onDestroy(() => {
      clearTimeout(timer);
      observer.disconnect();
    });
  }

  // ─────────────────────────────────────────────────────────────
  // CHART RENDERING & UPDATES
  // ─────────────────────────────────────────────────────────────

  /**
   * Draws (or redraws) the red velocity-profile line from a flat velocity array.
   * Also wires up the crosshair hover events, which read the same data array via closure.
   * @param data Flat velocity array (one value per depth index).
   */
  private plotVerticalProfile(data: any[]) {
    let [yScale, xScale] = untracked(() => [this.yScale(), this.xScale()]);
    // line generator
    const line = d3
      .line<number>()
      .defined((d) => d !== null && d !== undefined && !isNaN(d))
      .x((d) => (d !== null && d !== undefined ? xScale(d) : 0))
      .y((_, index) => yScale(index) || 0)
      .curve(d3.curveMonotoneY); // Smooths the line vertically
    // make / update path
    this.profileLine
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", "red")
      .attr("stroke-width", 2)
      .style("vector-effect", "non-scaling-stroke")
      .attr("d", line);


    // the mouse events for the crosshair
    this.chartGroup
      .select("rect.overlay")
      .on("mouseover", () => {
        if (this.showVelocityProfile())
          this.hoverCrosshairGroup.style("display", null);
      })
      .on("mouseout", () => this.hoverCrosshairGroup.style("display", "none"))
      .on("mousemove", (event) => {
        const [mx, my] = d3.pointer(event);
        // Read the *current* scales at event time — the depth/colour range
        // (and therefore xScale/yScale) can change after this handler was bound,
        // so the closure-captured scales would otherwise be stale and the dot
        // would track the old line position.
        const [xScale, yScale] = untracked(() => [this.xScale(), this.yScale()]);
        // Get the depth value under the mouse
        const transform = d3.zoomTransform(
          this.svgSelection.select("rect.overlay").node() as Element,
        );
        const xRescaled = transform.rescaleX(xScale);
        const yRescaled = transform.rescaleY(yScale);

        const depthIndex = Math.round(yRescaled.invert(my));
        if (depthIndex >= 0 && depthIndex < data.length) {
          const velocity = data[depthIndex];
          this.hoverCrosshairGroup
            .select("line.x-hover-line")
            .attr("transform", `translate(0, ${yRescaled(depthIndex)})`);
          this.hoverCrosshairGroup
            .select("line.y-hover-line")
            .attr("transform", `translate(${xRescaled(velocity)}, 0)`);
          this.hoverCrosshairGroup
            .select("circle")
            .attr("cx", xRescaled(velocity))
            .attr("cy", yRescaled(depthIndex));

          this.depth = depthIndex * this.dx();
          this.profileValue = data[depthIndex];
        }
      });
  }

  /** Binds sonic log data as steelblue dots onto `sonicDotsGroup`. */
  private renderSonicDots(
    log: { velocity: number; depth: number }[],
    xScale: d3.ScaleLinear<number, number>,
    yScale: d3.ScaleLinear<number, number>,
  ) {
    this.sonicDotsGroup
      .selectAll<SVGCircleElement, { velocity: number; depth: number }>("circle")
      .data(log)
      .join("circle")
      .attr("cx", (d) => xScale(d.velocity))
      .attr("cy", (d) => yScale(d.depth / this.dx()))
      .attr("r", 2)
      .attr("fill", "steelblue")
      .style("vector-effect", "non-scaling-stroke");
  }

  /**
   * Draws the blue sonic-log path and formation markers from well-log data.
   * If `logData` is null, clears the sonic line.
   * @param logData Processed log data containing the line points and depth markers.
   */
  private plotWellLog(logData: {
    log: { velocity: number; depth: number }[];
    markers: LogMarkers[];
  }) {
    if (!logData) {
      // clean the sonic container
      this.sonicLine.datum([]).attr("d", null);
      this.sonicDotsGroup.selectAll("circle").remove();
      this.wellMarkers.set([]);
    } else {
      // populate the sonic container
      const { log, markers } = logData;

      const [xScale, yScale] = untracked(() => [this.xScale(), this.yScale()]);

      this.sonicLineGenerator = d3
        .line<{ depth: number; velocity: number }>()
        .x((d) => xScale(d.velocity))
        .y((d) => yScale(d.depth / this.dx()))
        .curve(d3.curveMonotoneY);
      const GAP_THRESHOLD = 100;
      if (!untracked(() => this.viewerSettingsService.showSonicSamples())) {
        this.sonicLineGenerator.defined((d: any, i, arr) => {
          if (i === 0) return true;
          const prev: any = arr[i - 1];
          return d.depth - prev.depth < GAP_THRESHOLD;
        });
      }

      this.sonicLine
        .datum(log)
        .attr("d", this.sonicLineGenerator) // Only draw if we have points
        .attr("fill", "none")
        .attr("stroke", "steelblue")
        .attr("stroke-width", 1)
        .style("vector-effect", "non-scaling-stroke")
        .raise();
      this.profileLine?.raise();

      if (untracked(() => this.viewerSettingsService.showSonicSamples())) {
        this.renderSonicDots(log, xScale, yScale);
      } else {
        this.sonicDotsGroup.selectAll("circle").remove();
      }

      // marker now. the visibility has to be seeded from the settings box
      // rather than defaulted to true, otherwise reloading the log (a job
      // change while synchronising, say) switches unticked markers back on
      const [markersVisibility, showWellMarkers] = untracked(() => [
        this.jobStateService.markersVisibility(),
        this.viewerSettingsService.showWellMarkers(),
      ]);
      this.wellMarkers.set(
        markers
          ? markers.map((m) => ({
              ...m,
              visibility:
                showWellMarkers && isMarkerVisible(markersVisibility, m.m),
            }))
          : [],
      );
    }
  }

  /**
   * Uses D3's enter/update/exit pattern to efficiently sync formation marker
   * lines and labels to the current `wellMarkers` signal value.
   * Positions are animated with a 500 ms transition on depth changes.
   */
  private renderWellMarkers(markers: WellMarker[]) {
    // Grab the data without tracking
    const [chartWidth, dx, yScale] = untracked(() => [
      this.width(),
      this.dx(),
      this.yScale(),
    ]);

    // 1. SELECT & BIND DATA
    const markerSelection = this.sonicMarkersG
      .selectAll<SVGGElement, WellMarker>(".marker-item")
      .data(markers, (d) => (d ? d.z : null));

    // 2. ENTER PHASE: only runs for brand-new data points
    const enterGroups = markerSelection
      .enter()
      .append("g")
      .attr(
        "class",
        (d) => `marker-item marker-${d.m.replace(/[^a-z0-9-_]/gi, "-")}`,
      )
      .attr("transform", (d) => `translate(0, ${yScale(d.z / dx)})`);

    // Append static child elements only to the new groups (no DOM bloat on update)
    enterGroups
      .append("line")
      .attr("class", "marker-line")
      .attr("stroke", "#9ca3af")
      .attr("stroke-width", 1.5)
      .attr("stroke-dasharray", "4,4");

    enterGroups
      .append("text")
      .attr("class", "marker-label")
      .attr("fill", "#4b5563")
      .attr("font-size", "8px")
      .attr("font-family", "sans-serif")
      // Move static attributes to Enter phase to save processing on Update
      .attr("dominant-baseline", "middle")
      .attr("text-anchor", "start")
      .attr("vector-effect", "non-scaling-size");

    // 3. UPDATE PHASE: merge new and existing groups, then animate to new depth
    const allGroups = enterGroups.merge(markerSelection);

    // Animate all groups to their new depth
    allGroups
      .transition()
      .duration(500)
      .attr("transform", (d) => `translate(0, ${yScale(d.z / dx)})`)
      .style("opacity", (d) => (d.visibility === false ? 0 : 1));

    allGroups.style("pointer-events", (d) =>
      d.visibility === false ? "none" : "all",
    );

    // Update dynamic internal elements
    allGroups
      .select(".marker-label")
      // x and y can be 0 here since transform handles the movement
      .attr("x", 0)
      .attr("y", 0)
      .text((d) => d.m);

    allGroups
      .select(".marker-line")
      .attr("x1", (d: any, i: number, nodes: any[]) => {
        const currentLine = nodes[i];
        const parentNode = currentLine.parentNode as SVGGElement;

        const textNode = d3
          .select(parentNode)
          .select(".marker-label")
          .node() as SVGTextElement;

        // Start the line just after the label text ends
        const textWidth = textNode ? textNode.getBBox().width : 0;
        const gap = 5;

        return textWidth + gap;
      })
      .attr("x2", chartWidth);

    // 4. EXIT PHASE: fade out and remove stale markers
    markerSelection
      .exit()
      .transition()
      .duration(500)
      .style("opacity", 0)
      .remove();
  }

  /**
   * Smoothly transitions both data lines to reflect updated x/y scales.
   * Called from the axes effect via `untracked` so data changes don't retrigger it.
   * Also re-renders well markers after a scale change.
   * @param xScale Updated x scale (possibly after zoom).
   * @param yScale Updated y scale (possibly after zoom).
   */
  private scaleChangeLineUpdates(
    xScale: d3.ScaleLinear<number, number, never>,
    yScale: d3.ScaleLinear<number, number, never>,
  ) {
    // Peek at the data without tracking it so this effect doesn't fire on data changes
    const currentData = this.profileData();
    // If the line property doesn't exist, or there's no data bound to it yet, stop here.
    if (!this.profileLine || !currentData || currentData.length === 0) {
      return;
    }
    // update the profile line smoothly
    this.profileLine
      .transition()
      .duration(500)
      .attr(
        "d",
        d3
          .line<number>()
          .defined((d) => d !== null && d !== undefined && !isNaN(d))
          .x((d) => (d !== null && d !== undefined ? xScale(d) : 0))
          .y((_, index) => yScale(index) || 0)
          .curve(d3.curveMonotoneY),
      );

    // Transition sonic dots to new scale positions (data already bound; just update attrs)
    this.sonicDotsGroup
      .selectAll<SVGCircleElement, { velocity: number; depth: number }>("circle")
      .transition()
      .duration(500)
      .attr("cx", (d) => xScale(d.velocity))
      .attr("cy", (d) => yScale(d.depth / this.dx()));

    // there being a value for the well log resouce means that the sonic line exists and can / should be 'scaled'
    // same logic as above for the profile values
    if (!this.wellLogResource.error() && this.wellLogResource.value()) {
      this.sonicLineGenerator
        .x((d) => xScale(d.velocity))
        .y((d) => yScale(d.depth / this.dx()));
      this.sonicLine
        .transition()
        .duration(500)
        .attr("d", this.sonicLineGenerator);
    }

    const dx = this.dx()
    const markerSelection = this.sonicMarkersG
      .selectAll<SVGGElement, WellMarker>(".marker-item")
    markerSelection
      .transition()
      .duration(500)
      .attr("transform", (d) => `translate(0, ${yScale(d.z / dx)})`);
  }

  /**
   * Repositions the horizontal depth-indicator line (`hLine`) to the current active depth.
   * Tracked on `yScale` (reacts to depth-range changes) but reads xScale without tracking
   * to avoid double-firing on x changes.
   */
  private makeIndicatorLines(yScale: d3.ScaleLinear<number, number, never>) {
    // this one is actaully tracked by the yScale
    // but not xScale
    const xScale = untracked(() => this.xScale());

    if (!this.hLine) return;
    const yPos = yScale(this.jobStateService.depth());
    const [xMin, xMax] = xScale.range();
    this.hLine
      .attr("x1", xMin)
      .attr("x2", xMax)
      .attr("y1", yPos)
      .attr("y2", yPos);
  }

  /**
   * Renders both axis groups and triggers a grid redraw.
   * Accepts pre-computed (possibly zoom-rescaled) scales so zooming re-uses the same method.
   * @param xScale D3 linear scale for velocity (x-axis).
   * @param yScale D3 linear scale for depth (y-axis).
   * @param unit   Display unit for depth tick labels.
   */
  private renderAxes(
    xScale: d3.ScaleLinear<number, number>,
    yScale: d3.ScaleLinear<number, number>,
    unit: "km" | "kft",
  ) {
    let ticks = this.ticksResolution(yScale.domain(), this.dx(), unit);
    this.xAxisGroup
      .call(d3.axisBottom(xScale))
      .attr("transform", `translate(0,${this.height()})`);
    this.yAxisGroup.call(
      d3
        .axisLeft(yScale)
        .tickValues(ticks)
        .tickFormat((d) => {
          return (Number(d) * this.dx() * 0.001 * (unit == "km" ? 1 : 3.28084))
            .toFixed(1)
            .toString();
        }),
    );
    this.renderGrid(xScale, yScale);
  }

  /**
   * Clears and redraws light dashed grid lines behind the chart data.
   * Shares tick positions with the axes by reusing the same scales.
   */
  private renderGrid(
    xScale: d3.ScaleLinear<number, number>,
    yScale: d3.ScaleLinear<number, number>,
  ) {
    const xGrid = d3
      .axisBottom(xScale)
      .tickSize(-this.height())
      .tickFormat(() => "");

    const yGrid = d3
      .axisLeft(yScale)
      .tickSize(-this.width())
      .tickFormat(() => "");

    // DO NOT remove existing elements. D3 automatically updates them!
    this.xGridGroup
      .attr("transform", `translate(0, ${this.height()})`)
      .call(xGrid);

    this.yGridGroup.call(yGrid);

    // This targets the specific lines D3 just generated/updated
    this.xGridGroup
      .selectAll(".tick line")
      .attr("stroke", "#e0e0e0")
      .style("stroke-dasharray", "3,3");

    this.yGridGroup
      .selectAll(".tick line")
      .attr("stroke", "#e0e0e0")
      .style("stroke-dasharray", "3,3");

    // hide the solid outer spine (the domain line)
    this.xGridGroup.select(".domain").remove();
    this.yGridGroup.select(".domain").remove();
  }

  /**
   * Handles D3 zoom/pan events: rescales axes and repositions content via transform.
   * `profileLine` and `sonicLine` are translated directly to stay with the zoomable group.
   * @param event D3 native zoom event.
   */
  private handleZoom(event: d3.D3ZoomEvent<SVGSVGElement, any>) {
    const transform = event.transform;
    const { k } = event.transform;
    // Rescale the scales based on the zoom transform
    const newX = transform.rescaleX(this.xScale());
    const newY = transform.rescaleY(this.yScale());
    // Update the Axes
    this.renderAxes(newX, newY, this.viewerSettingsService.axisUnits());
    this.zoomableContentGroup
      .selectChildren("g")
      .attr("transform", transform.toString());
    // we can also do it by selecting paths inside the zoomableContentGroup, or * children in it, but slightly optimizing this way. might not make any difference tho
    this.profileLine.attr("transform", transform.toString());
    this.sonicLine.attr("transform", transform.toString());
  }

  // ─────────────────────────────────────────────────────────────
  // UTILITIES
  // ─────────────────────────────────────────────────────────────

  /**
   * Picks a human-readable tick interval for the depth axis so that no more
   * than ~15 ticks appear regardless of the visible depth range.
   * @param values [domainMin, domainMax] in depth-index units.
   * @param dx     Cell size in metres.
   * @param units  Target unit for display (km or kft).
   * @returns Array of depth-index tick values.
   */
  private ticksResolution(
    values: number[],
    dx: number,
    units: "kft" | "km" = "km",
  ) {
    // we do the min max cause the 'ranges' can be reversed hence the values could be in either order
    // this prolly won't happen for the vertical profile but sort of like just an IN CASE scenario
    let startValue = Math.min(...values);
    let endValue = Math.max(...values);
    let unit_dx = dx * (units == "km" ? 1 : 3.28084); // the dx will be in kft or km depending on the users selected choice
    let applicableRange = (endValue - startValue) * unit_dx;
    let options = [100, 200, 500, 1000, 2000, 2500, 5000, 10000]; // options for intervals
    let i = 0;

    // Choose the best interval option
    while (i < options.length - 1 && applicableRange / options[i] >= 15) {
      i++;
    }
    let selectedInterval = options[i];
    let countTicks = Math.floor(applicableRange / selectedInterval);
    let dxInterval = selectedInterval / unit_dx;

    // Initialize ticks array
    let ticks = [0];
    for (let j = 1; j <= countTicks; j++) {
      ticks.push(startValue + j * dxInterval);
    }
    return ticks;
  }

  /** Pushes newly calculated velocity min/max into the shared service to keep x-axis bounds current. */
  private updateVelocityMinMax(min: number, max: number) {
    this.verticalProfileService.xMin.set(min);
    this.verticalProfileService.xMax.set(max);
  }

  // ─────────────────────────────────────────────────────────────
  // TEMPLATE ACTIONS
  // ─────────────────────────────────────────────────────────────

  /** Resets zoom to the identity transform with a short animation. */
  protected resetZoom() {
    this.chartGroup
      .select("rect.overlay")
      .transition()
      .duration(300)
      .call(this.zoomBehavior.transform as any, d3.zoomIdentity);
  }

  /**
   * Generic helper used by template number inputs to write a validated number
   * back into any `WritableSignal<number>`.
   * Falls back to `_default` when the input is empty or zero (useful for
   * fields that use `nzMax` to clamp the value).
   */
  protected setValueOfSignal(
    event: Event,
    signal_to_set: WritableSignal<number>,
    _default: number = null,
  ) {
    let el = event.target as HTMLInputElement;
    let val = Number(el.value);
    if (!val && _default !== null) {
      // here we set the default, if availble for the field
      // this is to allow working with the nzMax scenario
      signal_to_set.set(_default);
      return;
    }
    if (!isNaN(val)) {
      signal_to_set.set(val);
    }
  }

  /** Captures the chart card as a PNG and downloads it with a structured filename. */
  protected downloadVerticalProfile() {
    let element = document.getElementById("vertical-profile-element-container");
    toPng(element, {
      quality: 1.0,
      pixelRatio: window.devicePixelRatio,
    })
      .then((dataUrl) => {
        const link = document.createElement("a");
        link.href = dataUrl;
        var name: string;
        if (this.jobStateService.jobData().modelGrid.is2d) {
          name =
            stripDash(
              this.jobStateService.jobData().projectName.substring(0, 7),
            ) +
            "-" +
            stripDash(this.jobStateService.jobData().name.substring(0, 7)) +
            "-CPNUM" +
            this.jobStateService.iteration() +
            "-" +
            this.jobStateService.jobData().id +
            ".png";
        } else {
          name =
            stripDash(
              this.jobStateService.jobData().projectName.substring(0, 7),
            ) +
            "-" +
            stripDash(this.jobStateService.jobData().name.substring(0, 7)) +
            "-IL" +
            this.jobStateService.inline() +
            "-XL" +
            this.jobStateService.crossline() +
            "-CPNUM" +
            this.jobStateService.iteration() +
            "-" +
            this.jobStateService.selectedModel() +
            "-" +
            this.jobStateService.jobData().id +
            ".png";
        }

        link.download = name;
        link.click();
      })
      .catch((error) => {
        console.error("Error generating screenshot:", error);
      });
  }
}
